import fileSaver from 'file-saver';
import productPurchaseOrderTodoType from './productPurchaseOrderTodo.type';
import {
  fetchPurchaseOrderTodosList as fetchPurchaseOrderTodosListService,
  updateActualFields,
  deletePurchaseOrderTodo as deletePurchaseOrderTodoService,
  bulkDeletePurchaseOrderTodos as bulkDeletePurchaseOrderTodosService,
  exportSelectedPurchaseOrderTodos,
  exportAllPurchaseOrderTodos,
  getPresignedUrlForPOUpload as getPresignedUrlService,
  moveErrorSheetToUpdate as moveErrorSheetToUpdateService,
} from '../../../../../services/productPurchaseOrderTodo.service';
import { downloadPOSampleExcel } from '../../../../../services/productPurchaseOrder.service';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';

/**
 * Download a file from a presigned S3 URL and save it locally with the given name
 * @param {string} fileUrl - Presigned S3 download URL
 * @param {string} fileName - Name to save the downloaded file as
 * @returns {Promise<void>}
 */
const downloadFileFromUrl = async (fileUrl, fileName) => {
  const response = await fetch(fileUrl);
  const blob = await response.blob();
  fileSaver.saveAs(blob, fileName);
};

// Fetch purchase order todos list
export const fetchPurchaseOrderTodosList = (params, t) => async (dispatch) => {
  const requestId = params.requestId || Date.now().toString();
  try {
    const response = await fetchPurchaseOrderTodosListService(params);

    if (response?.code === 200) {
      dispatch({
        type: productPurchaseOrderTodoType.FETCH_PURCHASE_ORDER_TODOS_LIST,
        payload: {
          data: response.data?.data || [],
          totalCount: response.data?.systemTotalCount || 0, // Use system-wide count instead of filtered count
          append: params?.append || false,
          requestId,
        },
      });
      return response.data;
    }
    const errorMessage =
      response?.errMsg || response?.data?.errMsg || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.GENERIC_ERROR') : 'An error occurred');
    toasterMsg(errorMessage, 'danger');
    return false;
  } catch (err) {
    const errorMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.GENERIC_ERROR') : err.toString();
    toasterMsg(errorMessage, 'danger');
    return null;
  }
};

// Update purchase order todo actual fields
export const updateProductPurchaseOrderTodoActualFields =
  ({ orderId, updates, showSuccessMessage = true, t }) =>
  async (dispatch) => {
    try {
      const response = await updateActualFields(orderId, updates);

      if (response?.code === 200) {
        dispatch({
          type: productPurchaseOrderTodoType.UPDATE_PURCHASE_ORDER_TODO_ACTUAL_FIELDS,
          payload: {
            orderId,
            updates,
          },
        });
        if (showSuccessMessage) {
          const successMessage = t
            ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATED')
            : response.message?.toString() || 'Actual fields updated successfully';
          toasterMsg(successMessage, 'success');
        }
        return response;
      }
      if (response && response.code !== 200) {
        const errorMessage = t
          ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATE_ERROR')
          : response.message?.toString() || 'Failed to update actual fields';
        toasterMsg(errorMessage, 'danger');
      }
      return response;
    } catch (err) {
      const errorMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATE_ERROR') : err.toString();
      toasterMsg(errorMessage, 'danger');
      return null;
    }
  };

// Batch update purchase order todo actual fields
export const batchUpdateProductPurchaseOrderTodoActualFields =
  ({ updates, t }) =>
  async (dispatch) => {
    try {
      const updatePromises = [];
      let updateCount = 0;

      Object.entries(updates).forEach(([orderId, orderUpdates]) => {
        const { childUpdates, ...parentUpdates } = orderUpdates;

        if (Object.keys(parentUpdates).length > 0) {
          updateCount += 1;
          updatePromises.push(
            dispatch(
              updateProductPurchaseOrderTodoActualFields({
                orderId,
                updates: parentUpdates,
                showSuccessMessage: false,
                t,
              }),
            ),
          );
        }

        if (childUpdates && Object.keys(childUpdates).length > 0) {
          Object.entries(childUpdates).forEach(([orderDetailsId, childUpdateValues]) => {
            updateCount += 1;
            const processedChildUpdates = { ...childUpdateValues };
            processedChildUpdates.orderDetailsId = orderDetailsId;
            updatePromises.push(
              dispatch(
                updateProductPurchaseOrderTodoActualFields({
                  orderId,
                  updates: processedChildUpdates,
                  showSuccessMessage: false,
                  t,
                }),
              ),
            );
          });
        }
      });

      const results = await Promise.all(updatePromises);
      const allSuccessful = results.every((result) => result?.code === 200);

      if (allSuccessful && updateCount > 0) {
        const successMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATED') : 'Actual fields updated successfully';
        toasterMsg(successMessage, 'success');
        return { success: true, results };
      }

      if (updateCount > 0) {
        const failedResults = results.filter((result) => result?.code !== 200);
        failedResults.forEach((result) => {
          const errorMessage = t
            ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATE_ERROR')
            : result?.message || 'Failed to update actual fields';
          toasterMsg(errorMessage, 'danger');
        });
      }

      return { success: allSuccessful, results };
    } catch (updateError) {
      const errorMessage = t
        ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ACTUAL_FIELDS_UPDATE_ERROR')
        : updateError?.message || 'Failed to update actual fields';
      toasterMsg(errorMessage, 'danger');
      return { success: false, error: updateError };
    }
  };

// Delete single purchase order todo
export const deleteProductPurchaseOrderTodo = (todoId, t) => async (dispatch) => {
  try {
    const response = await deletePurchaseOrderTodoService(todoId);

    if (response?.code === 200) {
      dispatch({
        type: productPurchaseOrderTodoType.DELETE_PURCHASE_ORDER_TODO,
        payload: { todoId },
      });
      const successMessage =
        response.message?.toString() || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.DELETE_SUCCESS_MESSAGE') : 'Deleted successfully');
      toasterMsg(successMessage, 'success');
      return response;
    }
    return response;
  } catch (err) {
    const errorMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.GENERIC_ERROR') : err.toString();
    toasterMsg(errorMessage, 'danger');
    return null;
  }
};

// Bulk delete purchase order todos
export const bulkDeleteProductPurchaseOrderTodos = (todoIds, t) => async (dispatch) => {
  try {
    const response = await bulkDeletePurchaseOrderTodosService(todoIds);

    if (response?.code === 200) {
      dispatch({
        type: productPurchaseOrderTodoType.BULK_DELETE_PURCHASE_ORDER_TODOS,
        payload: { todoIds },
      });
      const successMessage =
        response.message?.toString() || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.BULK_DELETE_COMPLETED') : 'Bulk delete completed successfully');
      toasterMsg(successMessage, 'success');
      return response;
    }
    return response;
  } catch (err) {
    const errorMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.GENERIC_ERROR') : err.toString();
    toasterMsg(errorMessage, 'danger');
    return null;
  }
};

// Set filter values
export const setFilterValues = (filterValues) => ({
  type: productPurchaseOrderTodoType.SET_FILTER_VALUES,
  payload: filterValues,
});

// Clear reducer
export const clearProductPurchaseOrderTodoReducer = () => ({
  type: productPurchaseOrderTodoType.CLEAR_REDUCER,
});

// Download Purchase Order Sample Excel
export const downloadPOSampleSheet =
  (params = {}, t) =>
  async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const downloadParams = {
        ...params,
        timezone,
      };
      const blob = await downloadPOSampleExcel(downloadParams);
      if (blob) {
        const fileName = `Sample Purchase Order_${new Date().getTime()}.xlsx`;
        fileSaver.saveAs(blob, fileName);
        const successMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SAMPLE_EXCEL_DOWNLOAD_SUCCESS') : 'Sample Excel downloaded successfully';
        toasterMsg(successMessage, 'success');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errMsg ||
        error?.message ||
        (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SAMPLE_EXCEL_DOWNLOAD_ERROR') : 'Failed to download sample Excel');
      toasterMsg(errorMessage, 'danger');
    }
  };

// Download Selected Purchase Order Todos Excel
export const downloadSelectedPurchaseOrderTodos = (selectedIds, t) => async () => {
  try {
    if (!selectedIds || !Array.isArray(selectedIds) || selectedIds.length === 0) {
      const warningMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SELECT_RECORDS_TO_DOWNLOAD') : 'Please select records to download';
      toasterMsg(warningMessage, 'warning');
      return;
    }

    const response = await exportSelectedPurchaseOrderTodos(selectedIds);
    if (response?.code === 200 && response?.data?.zipUrl) {
      const fileName = response.data.fileName || `Selected_Purchase_Orders_${new Date().getTime()}.xlsx`;
      await downloadFileFromUrl(response.data.zipUrl, fileName);
      const successMessage = t
        ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.DOWNLOAD_SELECTED_SUCCESS', { count: selectedIds.length })
        : `Downloaded ${selectedIds.length} selected records successfully`;
      toasterMsg(successMessage, 'success');
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errMsg ||
      error?.message ||
      (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.DOWNLOAD_SELECTED_ERROR') : 'Failed to download selected records');
    toasterMsg(errorMessage, 'danger');
  }
};

// Download All Purchase Order Todos Excel with current filters and search
export const downloadAllPurchaseOrderTodos =
  (params = {}, t) =>
  async () => {
    try {
      const response = await exportAllPurchaseOrderTodos(params);
      if (response?.code === 200 && response?.data?.zipUrl) {
        const fileName = response.data.fileName || `All_Purchase_Orders_${new Date().getTime()}.xlsx`;
        await downloadFileFromUrl(response.data.zipUrl, fileName);
        const successMessage = t ? t("PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.DOWNLOAD_ALL_SUCCESS") : "Downloaded all purchase order records successfully";
        toasterMsg(successMessage, "success");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errMsg ||
        error?.message ||
        (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.DOWNLOAD_ALL_ERROR') : 'Failed to download all records');
      toasterMsg(errorMessage, 'danger');
    }
  };

// Get presigned URL for uploading PO file to S3
export const getPresignedUrlForPOUpload = (fileName, t) => async () => {
  try {
    const response = await getPresignedUrlService(fileName);
    if (response?.code === 200 && response?.data?.presignedUrl) {
      return response.data;
    }
    const errorMessage = response?.errMsg || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.PRESIGNED_URL_ERROR') : 'Failed to get presigned URL');
    toasterMsg(errorMessage, 'danger');
    return null;
  } catch (error) {
    const errorMessage = error?.message || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.PRESIGNED_URL_GET_ERROR') : 'Error getting presigned URL');
    toasterMsg(errorMessage, 'danger');
    return null;
  }
};

// Upload file directly to S3 using presigned URL
export const uploadPOFileToS3 = (presignedUrl, file, uploadHeaders, t) => async () => {
  try {
    const headers = {
      'Content-Type': file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ...uploadHeaders, // Include metadata headers for Lambda to extract company/user info
    };

    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers,
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    const successMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.FILE_UPLOAD_SUCCESS') : 'Sheet uploaded successfully';
    toasterMsg(successMessage, 'success');
    return true;
  } catch (error) {
    const errorMessage = error?.message || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.FILE_UPLOAD_ERROR') : 'Failed to upload file');
    toasterMsg(errorMessage, 'danger');
    return false;
  }
};

// Move error sheet from Lambda_PO_Error/ to Lambda_PO/confirmed/ to trigger update
export const moveErrorSheetToUpdate =
  ({ errorSheetKey, originalKey }, t) =>
  async () => {
    try {
      if (!errorSheetKey) {
        const errorMessage = t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.ERROR_SHEET_KEY_REQUIRED') : 'Error sheet key is required';
        toasterMsg(errorMessage, 'danger');
        return null;
      }

      const response = await moveErrorSheetToUpdateService({ errorSheetKey, originalKey });

      if (response?.code === 200) {
        // Success toaster handled in socket.js after dispatch
        return response;
      }

      const errorMessage =
        response?.errMsg || response?.message || (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.UPDATE_TRIGGER_FAILED') : 'Failed to trigger update');
      toasterMsg(errorMessage, 'danger');
      return response;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errMsg ||
        error?.message ||
        (t ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.UPDATE_TRIGGER_FAILED') : 'Failed to trigger update');
      toasterMsg(errorMessage, 'danger');
      return null;
    }
  };
